import {
  DISPATCH_RADIO_SYSTEM_OPTIONS,
  STAFFED_OPTIONS,
  TYPE_OF_AGENCY_OPTIONS,
  formatUsPhoneTyping,
  STAKEHOLDER_COMMANDER,
  type RegistrationExtendedFormValues,
} from "@buxus/shared";
import { RegReqLabel } from "../../ui/RegReqLabel";
import { YesNoRadios } from "./YesNoRadios";

/** Set true to show Website on the Agency step again. */
const SHOW_WEBSITE_FIELD = false;

type AgencyInfoField =
  | "typeOfAgency"
  | "nameOfAgencyCommandingOfficer"
  | "nonEmergencyNumberForAgencyOrDepartment"
  | "website"
  | "staffed"
  | "numberOfPeopleInDepartment"
  | "dispatchRadioSystemsUsedByAgency"
  | "dispatchRadioSystemsUsedByAgencyOther"
  | "dispatchIsRadioSystemP25Compliant"
  | "dispatchIsRadioSystemEncrypted";

type Props = {
  extendedForm: RegistrationExtendedFormValues;
  stakeholderType: string;
  showFieldError: (field: AgencyInfoField) => string | undefined;
  onPatch: (partial: Partial<RegistrationExtendedFormValues>) => void;
  onBlur: (field: AgencyInfoField) => void;
};

export function AgencyInfoStep({
  extendedForm,
  stakeholderType,
  showFieldError,
  onPatch,
  onBlur,
}: Props) {
  const showCommandingOfficer = stakeholderType !== STAKEHOLDER_COMMANDER;
  const radioIsOther = extendedForm.dispatchRadioSystemsUsedByAgency.includes("Other");

  const toggleAgencyType = (value: string) => {
    const current = extendedForm.typeOfAgency;
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    onPatch({ typeOfAgency: next });
  };

  const toggleRadioSystem = (value: string) => {
    const current = extendedForm.dispatchRadioSystemsUsedByAgency;
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    if (!next.includes("Other")) {
      onPatch({
        dispatchRadioSystemsUsedByAgency: next,
        dispatchRadioSystemsUsedByAgencyOther: "",
      });
      return;
    }
    onPatch({ dispatchRadioSystemsUsedByAgency: next });
  };

  return (
    <>
      <h2 className="reg-step-title">Agency</h2>
      <div className="reg-grid">
        <div className="reg-field reg-field--full">
          <RegReqLabel required>Type of Agency</RegReqLabel>
          <div className="reg-checkbox-stack">
            {TYPE_OF_AGENCY_OPTIONS.map((option) => (
              <label key={option} className="reg-checkbox-label">
                <input
                  type="checkbox"
                  checked={extendedForm.typeOfAgency.includes(option)}
                  onChange={() => toggleAgencyType(option)}
                  onBlur={() => onBlur("typeOfAgency")}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          {showFieldError("typeOfAgency") ? (
            <small className="reg-field-error">{showFieldError("typeOfAgency")}</small>
          ) : null}
        </div>

        <label className="reg-field">
          <span>Station Name or Number</span>
          <input
            value={extendedForm.stationNameOrNumber}
            onChange={(e) => onPatch({ stationNameOrNumber: e.target.value })}
          />
        </label>

        {showCommandingOfficer ? (
          <label className="reg-field">
            <RegReqLabel required>Name of Commanding Officer or Superior</RegReqLabel>
            <input
              value={extendedForm.nameOfAgencyCommandingOfficer}
              onChange={(e) => onPatch({ nameOfAgencyCommandingOfficer: e.target.value })}
              onBlur={() => onBlur("nameOfAgencyCommandingOfficer")}
            />
            {showFieldError("nameOfAgencyCommandingOfficer") ? (
              <small className="reg-field-error">{showFieldError("nameOfAgencyCommandingOfficer")}</small>
            ) : null}
          </label>
        ) : null}

        <label className="reg-field">
          <RegReqLabel required>Non Emergency Number for Agency or Department</RegReqLabel>
          <input
            type="tel"
            value={extendedForm.nonEmergencyNumberForAgencyOrDepartment}
            onChange={(e) =>
              onPatch({
                nonEmergencyNumberForAgencyOrDepartment: formatUsPhoneTyping(e.target.value),
              })
            }
            onBlur={() => onBlur("nonEmergencyNumberForAgencyOrDepartment")}
          />
          {showFieldError("nonEmergencyNumberForAgencyOrDepartment") ? (
            <small className="reg-field-error">
              {showFieldError("nonEmergencyNumberForAgencyOrDepartment")}
            </small>
          ) : null}
        </label>

        {SHOW_WEBSITE_FIELD ? (
          <label className="reg-field">
            <span>Website</span>
            <input
              value={extendedForm.website}
              onChange={(e) => onPatch({ website: e.target.value })}
              onBlur={() => onBlur("website")}
            />
            {showFieldError("website") ? (
              <small className="reg-field-error">{showFieldError("website")}</small>
            ) : null}
          </label>
        ) : null}

        <label className="reg-field">
          <RegReqLabel required>Staffed</RegReqLabel>
          <select
            value={extendedForm.staffed}
            onChange={(e) => onPatch({ staffed: e.target.value })}
            onBlur={() => onBlur("staffed")}
          >
            <option value="">Select</option>
            {STAFFED_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {showFieldError("staffed") ? (
            <small className="reg-field-error">{showFieldError("staffed")}</small>
          ) : null}
        </label>

        <label className="reg-field">
          <RegReqLabel required>Number of people in department</RegReqLabel>
          <input
            type="number"
            min={0}
            value={extendedForm.numberOfPeopleInDepartment}
            onChange={(e) => onPatch({ numberOfPeopleInDepartment: e.target.value })}
            onBlur={() => onBlur("numberOfPeopleInDepartment")}
          />
          {showFieldError("numberOfPeopleInDepartment") ? (
            <small className="reg-field-error">{showFieldError("numberOfPeopleInDepartment")}</small>
          ) : null}
        </label>

        <div className="reg-field reg-field--full">
          <span>What type of radio system does your agency use?</span>
          <div className="reg-checkbox-stack">
            {DISPATCH_RADIO_SYSTEM_OPTIONS.map((option) => (
              <label key={option} className="reg-checkbox-label">
                <input
                  type="checkbox"
                  checked={extendedForm.dispatchRadioSystemsUsedByAgency.includes(option)}
                  onChange={() => toggleRadioSystem(option)}
                  onBlur={() => onBlur("dispatchRadioSystemsUsedByAgency")}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          {showFieldError("dispatchRadioSystemsUsedByAgencyOther") ? (
            <small className="reg-field-error">
              {showFieldError("dispatchRadioSystemsUsedByAgencyOther")}
            </small>
          ) : null}
        </div>

        {radioIsOther ? (
          <label className="reg-field">
            <RegReqLabel required>Please specify the radio system</RegReqLabel>
            <input
              value={extendedForm.dispatchRadioSystemsUsedByAgencyOther}
              onChange={(e) => onPatch({ dispatchRadioSystemsUsedByAgencyOther: e.target.value })}
              onBlur={() => onBlur("dispatchRadioSystemsUsedByAgencyOther")}
            />
            {showFieldError("dispatchRadioSystemsUsedByAgencyOther") ? (
              <small className="reg-field-error">
                {showFieldError("dispatchRadioSystemsUsedByAgencyOther")}
              </small>
            ) : null}
          </label>
        ) : null}

        <YesNoRadios
          name="dispatchIsRadioSystemP25Compliant"
          required
          label="Is your radio system P25 compliant?"
          value={extendedForm.dispatchIsRadioSystemP25Compliant}
          error={showFieldError("dispatchIsRadioSystemP25Compliant")}
          onChange={(v) => onPatch({ dispatchIsRadioSystemP25Compliant: v })}
          onBlur={() => onBlur("dispatchIsRadioSystemP25Compliant")}
        />

        <YesNoRadios
          name="dispatchIsRadioSystemEncrypted"
          required
          label="Is your radio system encrypted?"
          value={extendedForm.dispatchIsRadioSystemEncrypted}
          error={showFieldError("dispatchIsRadioSystemEncrypted")}
          onChange={(v) => onPatch({ dispatchIsRadioSystemEncrypted: v })}
          onBlur={() => onBlur("dispatchIsRadioSystemEncrypted")}
        />
      </div>
    </>
  );
}
